// ObjectiveModal.js

import React, { useMemo, useCallback } from 'react';
import { 
  Target, Search, Clock, Sparkles, Lightbulb, List, 
  CheckCircle, X, MessageCircle, Globe, HelpCircle, Zap 
} from 'lucide-react';
import { CurrentStepDisplay } from './ChatComponents';

// Composant Modal pour l'objectif avec étape courante
export const ObjectiveModal = ({ 
  show, 
  onClose, 
  userObjective, 
  searchObjective, 
  chatMessages, 
  savedSources,
  actionPlan,
  currentStepId,
  onStepChange,
  onStepComplete,
  forceUpdate = 0,
  competenceData,
  getStepNotes
}) => {
  // IMPORTANT : Tous les hooks doivent être appelés AVANT tout return conditionnel
  
  // MÊME LOGIQUE que ResultsManager.js pour récupérer les notes
  const getStepNotesData = useCallback((stepId) => {
    if (competenceData?.stepNotes && competenceData.stepNotes[stepId]) {
      return competenceData.stepNotes[stepId];
    } else if (getStepNotes) {
      return getStepNotes(stepId) || {};
    }
    return {};
  }, [competenceData, getStepNotes]);

  // MÊME LOGIQUE que calculateStepProgress dans ResultsManager.js
  const calculateStepMetrics = useCallback((step) => {
    const stepNotes = getStepNotesData(step.id);
    
    const metrics = {
      decisions: 0,
      questions: 0,
      recommendations: 0,
      completionScore: 0
    };

    if (stepNotes.decisions && stepNotes.decisions.trim().length > 0) {
      const sections = stepNotes.decisions.split('\n\n').filter(s => s.trim());
      metrics.decisions = sections.length;
    }

    if (stepNotes.questions && stepNotes.questions.trim().length > 0) {
      const sections = stepNotes.questions.split('\n\n').filter(s => s.trim());
      metrics.questions = sections.length;
    }

    if (stepNotes.ai_recommendations && stepNotes.ai_recommendations.trim().length > 0) {
      const sections = stepNotes.ai_recommendations.split('\n\n').filter(s => s.trim());
      metrics.recommendations = sections.length;
    }

    // Calcul du score de complétion (même logique que ResultsManager)
    if (step.termine) {
      metrics.completionScore = 100;
    } else {
      let score = 0;
      if (metrics.decisions > 0) score += 40;
      if (metrics.questions > 0) score += 30;
      if (metrics.recommendations > 0) score += 30;
      metrics.completionScore = Math.min(score, 95);
    }

    return metrics;
  }, [getStepNotesData]);

  // Calculer les statistiques de l'étape courante
  const getCurrentStepStats = useMemo(() => {
    if (!currentStepId || !actionPlan?.steps) {
      return { decisions: 0, questions: 0, recommendations: 0 };
    }

    const currentStep = actionPlan.steps.find(s => s.id === currentStepId);
    if (!currentStep) {
      return { decisions: 0, questions: 0, recommendations: 0 };
    }

    const metrics = calculateStepMetrics(currentStep);
    
    console.log("🔍 DEBUG Métriques étape courante:", {
      stepId: currentStepId,
      decisions: metrics.decisions,
      questions: metrics.questions,
      recommendations: metrics.recommendations,
      completionScore: metrics.completionScore
    });

    return {
      decisions: metrics.decisions,
      questions: metrics.questions,
      recommendations: metrics.recommendations
    };
  }, [currentStepId, actionPlan, calculateStepMetrics]);

  // Calculer la progression globale (MÊME LOGIQUE que ResultsManager.js)
  const globalProgress = useMemo(() => {
    if (!actionPlan?.steps || actionPlan.steps.length === 0) {
      return 0;
    }

    let totalProgress = 0;

    actionPlan.steps.forEach(step => {
      const metrics = calculateStepMetrics(step);
      totalProgress += metrics.completionScore;
    });

    const average = Math.round(totalProgress / actionPlan.steps.length);
    
    console.log("🔍 DEBUG Progression globale:", {
      totalSteps: actionPlan.steps.length,
      totalProgress,
      average
    });

    return average;
  }, [actionPlan, calculateStepMetrics]);

  const currentStep = actionPlan?.steps?.find(s => s.id === currentStepId);
  const completedSteps = actionPlan?.steps?.filter(s => s.termine).length || 0;
  const totalSteps = actionPlan?.steps?.length || 0;

  // MAINTENANT on peut faire le return conditionnel
  if (!show) return null;

  console.log("🔍 DEBUG ActionPlan complet:", actionPlan);
  console.log("🔍 DEBUG currentStepId:", currentStepId);
  console.log("🔍 DEBUG competenceData:", competenceData);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 flex items-center justify-center overflow-hidden">
                <Target size={12} className="text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Objectif du Projet</h3>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          
          <div className="space-y-6">
            {/* Objectif Principal */}
            {userObjective && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-center gap-3 mb-4">
                  <Target size={24} className="text-blue-600" />
                  <span className="text-lg font-semibold text-blue-700">Objectif Principal</span>
                </div>
                <p className="text-base text-gray-700 leading-relaxed">{userObjective}</p>
              </div>
            )}
            
            {/* Focus Recherche */}
            {searchObjective && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                <div className="flex items-center gap-3 mb-4">
                  <Search size={24} className="text-purple-600" />
                  <span className="text-lg font-semibold text-purple-700">Focus Recherche</span>
                </div>
                <p className="text-base text-gray-700 leading-relaxed">{searchObjective}</p>
              </div>
            )}

            {/* Plan d'Action - Vue d'ensemble */}
            {actionPlan && (
              <div key={`modal-plan-${forceUpdate}`} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                <div className="flex items-center gap-3 mb-4">
                  <List size={24} className="text-green-600" />
                  <span className="text-lg font-semibold text-green-700">Vue d'ensemble du Plan</span>
                  <span className="px-2 py-1 bg-green-200 text-green-700 rounded-full text-xs font-medium">
                    {totalSteps} étapes
                  </span>
                </div>
                
                {/* Sélecteur d'étape */}
                <div className="mb-4">
                  <select
                    value={currentStepId || ''}
                    onChange={(e) => onStepChange(parseInt(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Aucune étape sélectionnée</option>
                    {actionPlan.steps?.map((step) => (
                      <option key={step.id} value={step.id}>
                        {step.termine ? '✅' : '🔄'} Étape {step.id}: {step.titre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Statistiques générales du projet - 3 indicateurs */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-center gap-3 mb-4">
                <Lightbulb size={24} className="text-blue-600" />
                <span className="text-lg font-semibold text-blue-700">Vue d'ensemble du Projet</span>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white rounded-lg border border-blue-100">
                  <div className="text-2xl font-bold text-green-600">
                    {globalProgress}%
                  </div>
                  <div className="text-sm text-gray-600">Progression globale</div>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border border-blue-100">
                  <div className="text-2xl font-bold text-orange-600">
                    {completedSteps}/{totalSteps}
                  </div>
                  <div className="text-sm text-gray-600">Étapes terminées</div>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border border-blue-100">
                  <div className="text-2xl font-bold text-purple-600">{savedSources.length}</div>
                  <div className="text-sm text-gray-600">Sources disponibles</div>
                </div>
              </div>
            </div>

            {/* Statistiques de l'étape courante */}
            {currentStepId && currentStep && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                <div className="flex items-center gap-3 mb-4">
                  <Clock size={24} className="text-green-600" />
                  <span className="text-lg font-semibold text-green-700">Étape en cours : {currentStep.titre}</span>
                  {currentStep.termine && (
                    <span className="px-2 py-1 bg-green-600 text-white rounded-full text-xs font-medium">
                      ✓ Terminée
                    </span>
                  )}
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg border border-green-100">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div className="text-2xl font-bold text-green-600">
                        {getCurrentStepStats.decisions}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {getCurrentStepStats.decisions > 1 ? 'Décisions prises' : 'Décision prise'}
                    </div>
                  </div>
                  
                  <div className="text-center p-4 bg-white rounded-lg border border-orange-100">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <HelpCircle className="h-5 w-5 text-orange-600" />
                      <div className="text-2xl font-bold text-orange-600">
                        {getCurrentStepStats.questions}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {getCurrentStepStats.questions > 1 ? 'Questions & défis' : 'Question & défi'}
                    </div>
                  </div>
                  
                  <div className="text-center p-4 bg-white rounded-lg border border-purple-100">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Sparkles className="h-5 w-5 text-purple-600" />
                      <div className="text-2xl font-bold text-purple-600">
                        {getCurrentStepStats.recommendations}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {getCurrentStepStats.recommendations > 1 ? 'Recommandations' : 'Recommandation'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Message d'encouragement personnalisé */}
            {userObjective && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles size={20} className="text-indigo-600" />
                  <span className="font-semibold text-indigo-700">Votre parcours avec Aladinn</span>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  {actionPlan && currentStepId ? (
                    <>
                      Vous travaillez actuellement sur l'étape "{currentStep?.titre}". 
                      {getCurrentStepStats.decisions > 0 && (
                        ` Vous avez pris ${getCurrentStepStats.decisions} décision(s) pour cette étape.`
                      )}
                      {getCurrentStepStats.questions > 0 && (
                        ` ${getCurrentStepStats.questions} question(s) ou défi(s) identifié(s).`
                      )}
                      {getCurrentStepStats.recommendations > 0 && (
                        ` ${getCurrentStepStats.recommendations} recommandation(s) disponible(s).`
                      )}
                      {completedSteps > 0 && (
                        ` Vous avez déjà terminé ${completedSteps} étape(s) sur ${totalSteps}.`
                      )}
                      {savedSources.length > 0 && ` Vous disposez de ${savedSources.length} source(s) pour vous aider.`}
                    </>
                  ) : (
                    <>
                      Vous avez défini un objectif clair{actionPlan ? ` avec un plan d'action structuré en ${totalSteps} étapes` : ''}{savedSources.length > 0 ? ` et disposez de ${savedSources.length} sources pour enrichir vos recherches` : ''}.
                      {completedSteps > 0 && (
                        ` Vous avez déjà complété ${completedSteps} étape(s) sur ${totalSteps}.`
                      )}
                      {' '}Sélectionnez une étape pour commencer à travailler avec Aladinn !
                    </>
                  )}
                </p>
              </div>
            )}
          </div>
          
          <div className="mt-8 text-center">
            <button 
              onClick={onClose}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <span className="text-sm font-medium">Fermer</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};